/**
 * This example shows how you can use custom nodes and edges to dynamically add elements to your react flow graph.
 * A global layouting function calculates the new positions for the nodes every time the graph changes and animates existing nodes to their new position.
 *
 * There are three ways of adding nodes to the graph:
 *  1. Click an existing node: Create a new child node of the clicked node
 *  2. Click on the plus icon of an existing edge: Create a node in between the connected nodes of the edge
 *  3. Click a placeholder node: Turn the placeholder into a "real" node to prevent jumping of the layout
 *
 * The graph elements are added via hook calls in the custom nodes and edges. The layout is calculated every time the graph changes (see hooks/useLayout.ts).
 **/
import { onNodeDrag } from '@ui/lib/reactFlow/DragAndDrop/onNodeDrag'
import { onNodeDragStop } from '@ui/lib/reactFlow/DragAndDrop/onNodeDragStop'
import { createPlaceholderNode, defaultEdgeOptions, nodeHasChildren } from '@ui/lib/reactFlow/helpers'
import { useBlocker, useParams, useSearchParams } from 'react-router'
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  Node,
  NodeOrigin,
  Panel,
  ProOptions,
  ReactFlowProvider,
  useReactFlow,
  // useReactFlow,
} from 'reactflow'

import {
  DatableDataType,
  DatableLog,
  DatableTrace,
  NodeButtonAction,
  NodeButtonPosition,
  NodeTypeEnum,
  PanelEnum,
  StartAndEnd,
  TelemetryEvent,
} from '@ui//interfaces'
import useLayout from '@ui/lib/hooks/useLayout'
import edgeTypes from '@ui/pages/pipelinesWorkflow/components/EdgeTypes'
import {
  useExporterNodeStore,
  useNodeAndEdgeStore,
  useSaveUpdateStatusStore,
  useTelemetryStore,
  useTimeStore,
} from '@ui/stores'

import 'reactflow/dist/base.css'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createNodeInBetween, splitNode } from '@ui/lib/reactFlow/nodeMethods'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { Destination, NodeType } from '@ui/__generated__/graphql'
import { ActionModal, Button } from '@ui/components/core'
import { getStartAndEnd } from '@ui/lib/helpers/timeWindow.ts'
import useAppNavigation from '@ui/lib/hooks/useAppNavigation'
import { TelemetryQuery } from '@ui/loaders/telemetry.tsx'
import { DestinationForm } from '@ui/pages/destinations/components/DestinationForm'
import nodeTypes from '@ui/pages/pipelinesWorkflow/components/NodeTypes'
import { PipelineActionModal } from '@ui/pages/pipelinesWorkflow/components/PipelineActionModal'
import { ToggleEnableLoaf } from '@ui/pages/pipelinesWorkflow/components/SaveUpdateEnableLoaf'
import { RouteNamesEnum } from '@ui/router/routes'

import { Transform } from './Transform'

const proOptions: ProOptions = { account: 'paid-pro', hideAttribution: true }
const nodeOrigin: NodeOrigin = [0, 0]

function ReactFlowWrapper() {
  const { fitView } = useReactFlow()

  const {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onNodesDelete,
    onConnect,
    setNodes,
    setEdges,
    setRefreshLayout,
    setSwitchWorkflowUpdate,
  } = useNodeAndEdgeStore(
    useShallow((state) => ({
      nodes: state.nodes,
      edges: state.edges,
      onNodesChange: state.onNodesChange,
      onEdgesChange: state.onEdgesChange,
      onNodesDelete: state.onNodesDelete,
      onConnect: state.onConnect,
      setNodes: state.setNodes,
      setEdges: state.setEdges,
      setRefreshLayout: state.setRefreshLayout,
      switchWorkflowUpdate: state.switchWorkflowUpdate,
      setSwitchWorkflowUpdate: state.setSwitchWorkflowUpdate,
    }))
  )

  const { pipelineModified, setPipelineModified } = useSaveUpdateStatusStore(
    useShallow((state) => ({
      pipelineModified: state.pipelineModified,
      setPipelineModified: state.setPipelineModified,
    }))
  )

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    setNextLocation(nextLocation.pathname)
    return pipelineModified && currentLocation.pathname !== nextLocation.pathname
  })
  const [nextLocation, setNextLocation] = useState<string | null>(null)
  const [openModal, setOpenModal] = useState(false)

  // Logic:
  // If we get close to the left side of a node, we get added as a child
  // If we get close to the right side of a node, we get injected as a parent
  // If we get close to the top or bottom of a node, we get injected as a sibling
  const onNodeDragCallback = (_: React.MouseEvent, targetNode: Node) => {
    onNodeDrag(targetNode, nodes, edges, setNodes, setEdges)
  }

  const onNodeDragStopCallback = (_: React.MouseEvent, targetNode: Node) => {
    onNodeDragStop(targetNode, nodes, edges, setNodes, setEdges, setRefreshLayout)
  }

  const goToV2 = () => {
    setSwitchWorkflowUpdate(true)
  }

  fitView({ duration: 400 })

  useLayout()

  return (
    <>
      <ActionModal
        open={blocker.state === 'blocked' && pipelineModified && !openModal && !nextLocation?.includes('/pipelines')}
        title="Unsaved Changes on Pipeline"
        message="Are you sure you want to leave this page? You have unsaved changes that will be lost."
        handleAction={() => {
          if (blocker.reset) {
            blocker.reset()
            setOpenModal(false)
          }
        }}
        actionIdentifier="Stay"
        dismissAction={() => {
          setPipelineModified(false)
          if (blocker.proceed) {
            blocker.proceed()
          }
        }}
        dismissIdentifier="Continue"
      />
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onConnect={onConnect}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        proOptions={proOptions}
        onInit={() => ({})}
        fitView
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesDelete={onNodesDelete}
        onNodeDrag={onNodeDragCallback}
        onNodeDragStop={onNodeDragStopCallback}
        nodeOrigin={nodeOrigin}
        defaultEdgeOptions={defaultEdgeOptions}>
        <Background variant={BackgroundVariant.Dots} />
        <Controls />
        <Panel position="top-center">
          <ToggleEnableLoaf />
        </Panel>
        <Panel position="top-right">
          <div className="relative flex flex-col">
            <div className="absolute right-0 top-0 h-full w-[19rem] animate-pulse rounded-md bg-primary-500 shadow-md" />
            <div className="relative flex w-72 flex-col justify-end space-y-2 rounded-md bg-white p-4">
              <h1 className="text-xs font-medium">NEW WORKFLOW UPDATE</h1>
              <p className="text-xs">
                We&apos;ve added a new way to transform your data using steps. Click below to see what&apos;s new!
              </p>
              <Button size="small" variant={'primary-red'} onClick={goToV2}>
                <span className="text-xs">See new workflow</span>
              </Button>
            </div>
          </div>
        </Panel>
      </ReactFlow>
    </>
  )
}

export function PipelineWorkflow() {
  const { nodes, edges, displayModal, setDisplayModal, currentNodeId, setCurrentNodeId } = useNodeAndEdgeStore(
    (state) => ({
      nodes: state.nodes,
      edges: state.edges,
      displayModal: state.displayModal,
      setDisplayModal: state.setDisplayModal,
      currentNodeId: state.currentNodeId,
      setCurrentNodeId: state.setCurrentNodeId,
    })
  )

  const { setPipelineNodes } = useSaveUpdateStatusStore((state) => ({
    setPipelineNodes: state.setPipelineNodes,
  }))

  const setTemplate = useExporterNodeStore((state) => state.setTemplate)
  const setPipelineModified = useSaveUpdateStatusStore((state) => state.setPipelineModified)

  const updateNodeKeyValue = useNodeAndEdgeStore((state) => state.updateNodeKeyValue)

  const updateNodeType = useNodeAndEdgeStore((state) => state.updateNodeType)

  const nodeColor = (node: Node) => {
    if (node.id === currentNodeId) {
      //indigo-100
      return '#EDBDBD'
    } else {
      //default border color
      return '#e5e7eb'
    }
  }

  // This is for the minimap
  const nodeStrokeColor = (node: Node) => {
    if (node.id === currentNodeId) {
      //indigo-100
      return '#D05052'
    } else {
      //default border color
      return 'transparent'
    }
  }

  const onClickClose = () => {
    setCurrentNodeId(null)
  }

  const [searchParams] = useSearchParams()
  const { navigateTo } = useAppNavigation()
  const { instanceId, pipelineId } = useParams<{ instanceId: string; pipelineId: string }>()

  const nodeIdFromSearch = searchParams.get('nodeId') as string
  const positionFromSearch = searchParams.get('position') as NodeButtonPosition
  const actionFromSearch = searchParams.get('action') as NodeButtonAction

  const hasChildNodes = useMemo(() => {
    if (!currentNodeId) return false
    return nodeHasChildren(edges, currentNodeId)
  }, [currentNodeId, edges])

  useEffect(() => {
    if (nodeIdFromSearch && positionFromSearch && actionFromSearch) {
      const parentEdgeIds = edges.filter((edge) => edge.target === nodeIdFromSearch).map((edge) => edge.id)
      const childEdgeIds = edges.filter((edge) => edge.source === nodeIdFromSearch).map((edge) => edge.id)

      let newNodeId: string | null = null

      if (actionFromSearch === NodeButtonAction.SPLIT) {
        newNodeId = splitNode({
          edgeIds: childEdgeIds,
          type: NodeType.Code,
        }) as string
        // If a node gets split at the end of a pipeline with no placeholder, we need to create a place of the new node

        if (!hasChildNodes) {
          createPlaceholderNode({ currentNodeId: newNodeId })
        }
      } else if (actionFromSearch === NodeButtonAction.IN_BETWEEN) {
        newNodeId = createNodeInBetween({
          edgeIds: positionFromSearch === NodeButtonPosition.LEFT ? parentEdgeIds : childEdgeIds,
          position: positionFromSearch,
          type: NodeType.Code,
        })
      }

      setCurrentNodeId(newNodeId)
      setDisplayModal(PanelEnum.CODE)
      navigateTo(RouteNamesEnum.EXISTING_PIPELINE_NODE, { instanceId, pipelineId, nodeId: newNodeId })
    }

    if (nodeIdFromSearch && !positionFromSearch && !actionFromSearch) {
      setCurrentNodeId(nodeIdFromSearch)
      setDisplayModal(PanelEnum.CODE)
      navigateTo(RouteNamesEnum.EXISTING_PIPELINE_NODE, { instanceId, pipelineId, nodeId: nodeIdFromSearch })
    }
  }, [searchParams])

  useEffect(() => {
    setPipelineNodes(useNodeAndEdgeStore.getState().nodes)
  }, [setPipelineNodes])

  const timewindow = useTimeStore(useShallow((state) => state.timewindow))

  const [startAndEnd] = useState<StartAndEnd>(getStartAndEnd(timewindow))
  const { dataSource, whereClause, setLogs, setSpans, setUniqueKeysLogs, setUniqueKeysSpans } = useTelemetryStore(
    useShallow((state) => ({
      dataSource: state.dataSource,
      whereClause: state.whereClause,
      setLogs: state.setLogs,
      setSpans: state.setSpans,
      setUniqueKeysLogs: state.setUniqueKeysLogs,
      setUniqueKeysSpans: state.setUniqueKeysSpans,
    }))
  )

  const parentNodeId = useMemo(() => {
    const parentNode = nodes.find((n) => edges.find((e) => e.source === n.id && e.target === currentNodeId))
    return parentNode?.id
  }, [currentNodeId, edges, nodes])

  // TODO: Figure out why query is triggered twice, and the second request is cancelled
  const {
    data: telemetryQueryData,
    loading: telemetryQueryLoading,
    // error: telemetryQueryError,
  } = TelemetryQuery({
    from: dataSource,
    where: whereClause,
    limit: 100,
    nodeId: parentNodeId,
    ...startAndEnd,
  })

  const setEvents = useCallback(
    (dataSource: DatableDataType, events: TelemetryEvent<DatableLog | DatableTrace>[]) => {
      if (dataSource === 'logs') {
        setLogs(events as TelemetryEvent<DatableLog>[])
      } else {
        setSpans(events as TelemetryEvent<DatableTrace>[])
      }
    },
    [setLogs, setSpans]
  )

  useEffect(() => {
    if (telemetryQueryData && !telemetryQueryLoading) {
      setEvents(dataSource, telemetryQueryData.telemetry.events)
      if (dataSource === 'logs') {
        setUniqueKeysLogs(telemetryQueryData.telemetry.uniqueKeys)
      } else {
        setUniqueKeysSpans(telemetryQueryData.telemetry.uniqueKeys)
      }
    }
  }, [dataSource, setEvents, telemetryQueryData, telemetryQueryLoading])

  const saveToConfigAndClose = async (destination: Destination) => {
    const {
      id,
      name,
      template: { slug },
    } = destination

    updateNodeType(currentNodeId as string, NodeTypeEnum.EXPORT)
    updateNodeKeyValue(currentNodeId, {
      config: {},
      modified: true,
      exportType: slug || '',
      description: name || '',
      destinationId: id,
    })
    setPipelineModified(true)
    setDisplayModal(PanelEnum.HIDDEN)
    setCurrentNodeId(null)
    setTemplate(null)
  }

  const handleComponentShown = (displayModal: PanelEnum) => {
    switch (displayModal) {
      case PanelEnum.EXPORT:
        return (
          <DestinationForm
            onCreate={saveToConfigAndClose}
            onCancel={() => {
              setDisplayModal(PanelEnum.DESTINATIONS)
            }}
          />
        )
      case PanelEnum.CODE:
        return <Transform />
      default:
        return (
          <>
            <ReactFlowWrapper />
            <MiniMap
              nodeColor={nodeColor}
              nodeStrokeColor={nodeStrokeColor}
              className="border border-primary-300"
              position="bottom-right"
              onClick={onClickClose}
            />
          </>
        )
    }
  }

  const queryClient = new QueryClient()

  return (
    <ReactFlowProvider>
      <QueryClientProvider client={queryClient}>
        {handleComponentShown(displayModal)}
        <PipelineActionModal />
      </QueryClientProvider>
    </ReactFlowProvider>
  )
}
